import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Drawer, Empty } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { notificationAPI } from '../services/api';

const POLL_MS = 60000;

// appointmentsPath is null where the role has no appointments page (lab), so items stay unlinked.
export default function NotificationBell({ appointmentsPath }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await notificationAPI.getAll();
      setItems(data?.notifications || []);
      setUnread(data?.unread || 0);
      return data?.unread || 0;
    } catch {
      setUnread(0);
      return 0;
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);

  const show = async () => {
    setOpen(true);
    if (!(await load())) return;
    // Items keep their unread styling for this viewing; the next poll reflects the server's readAt.
    try { await notificationAPI.markRead(); setUnread(0); } catch { /* badge stays until the next poll */ }
  };

  const linkFor = n => (n.type === 'appointment' && appointmentsPath ? appointmentsPath : null);

  return <>
    <Badge count={unread} size="small" overflowCount={99}>
      <Button aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} title="Notifications" icon={<BellOutlined />} onClick={show} />
    </Badge>
    <Drawer title="Notifications" placement="right" open={open} onClose={() => setOpen(false)}>
      {items.length === 0 ? <Empty description="No notifications yet" /> : items.map(n => {
        const to = linkFor(n);
        return <div key={n._id}
          role={to ? 'link' : undefined} tabIndex={to ? 0 : undefined}
          onClick={to ? () => { setOpen(false); navigate(to); } : undefined}
          onKeyDown={to ? e => { if (e.key === 'Enter') { setOpen(false); navigate(to); } } : undefined}
          style={{ padding: '12px 0', borderBottom: '1px solid #F0F0F0', cursor: to ? 'pointer' : 'default' }}>
          <div style={{ fontWeight: n.readAt ? 400 : 600 }}>{!n.readAt && <Badge status="processing" style={{ marginRight: 8 }} />}{n.title}</div>
          {n.body && <div style={{ color: '#4B5563', marginTop: 4 }}>{n.body}</div>}
          <small style={{ color: '#6B7280' }}>{dayjs(n.createdAt).format('DD MMM YYYY, hh:mm A')}</small>
        </div>;
      })}
    </Drawer>
  </>;
}
